/**
 * Builder-only palette → grid interactions (does not rely on GridStack.setupDragIn).
 */

import {
  paintBuilderTrackOverlayFromPointer,
  clearBuilderTrackOverlayHighlight,
  clearBuilderPalettePlaceholder,
} from '../shared/scorer-layout-gridstack-adapter.js';

function readPaletteKey(el) {
  return String(el?.getAttribute?.('data-palette-key') || el?.dataset?.paletteKey || '').trim();
}

function isPointInside(el, clientX, clientY) {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
}

function buildDragGhost(sourceEl) {
  const ghost = sourceEl.cloneNode(true);
  ghost.classList.add('scorer-layout-palette-drag-ghost');
  ghost.setAttribute('data-alpine-ignore', 'true');
  ghost.style.position = 'fixed';
  ghost.style.zIndex = '10050';
  ghost.style.pointerEvents = 'none';
  ghost.style.opacity = '0.92';
  ghost.style.width = `${Math.max(120, sourceEl.offsetWidth)}px`;
  ghost.style.maxWidth = '240px';
  ghost.style.transform = 'rotate(-1deg)';
  return ghost;
}

/**
 * @param {object} opts
 * @param {import('gridstack').GridStack | null} opts.grid
 * @param {ParentNode} opts.root
 * @param {HTMLElement | null} [opts.trackOverlayEl]
 * @param {(paletteKey: string) => object | null} opts.resolveDef
 * @param {(payload: { def: object, x?: number, y?: number, clientX?: number, clientY?: number, autoPosition?: boolean }) => void} opts.onDrop
 */
export function mountBuilderPaletteDrag({ grid, root, trackOverlayEl = null, resolveDef, onDrop }) {
  const cleanups = [];

  const teardown = () => {
    clearBuilderTrackOverlayHighlight(trackOverlayEl);
    clearBuilderPalettePlaceholder(grid);
    while (cleanups.length) {
      try {
        cleanups.pop()?.();
      } catch {
        /* ignore */
      }
    }
  };

  const bind = () => {
    teardown();
    if (!grid || !root) return;

    const items = [...root.querySelectorAll('.scorer-layout-palette-item')];
    items.forEach((itemEl) => {
      itemEl.style.touchAction = 'none';

      const onPointerDown = (event) => {
        if (event.button !== 0) return;
        const paletteKey = readPaletteKey(itemEl);
        const def = paletteKey ? resolveDef(paletteKey) : null;
        if (!def) return;

        event.preventDefault();
        try {
          itemEl.setPointerCapture?.(event.pointerId);
        } catch {
          /* ignore */
        }

        const startX = event.clientX;
        const startY = event.clientY;
        let ghost = null;
        let dragging = false;
        let lastSlot = null;
        const spanW = def.defaultColSpan || 1;
        const spanH = def.defaultRowSpan || 1;

        const onPointerMove = (ev) => {
          const dx = ev.clientX - startX;
          const dy = ev.clientY - startY;
          if (!dragging && Math.hypot(dx, dy) < 3) return;
          dragging = true;
          if (!ghost) {
            ghost = buildDragGhost(itemEl);
            document.body.appendChild(ghost);
          }
          ghost.style.left = `${ev.clientX + 10}px`;
          ghost.style.top = `${ev.clientY + 10}px`;
          lastSlot = paintBuilderTrackOverlayFromPointer(trackOverlayEl, grid, ev.clientX, ev.clientY, spanW, spanH);
        };

        const finish = (ev) => {
          document.removeEventListener('pointermove', onPointerMove);
          document.removeEventListener('pointerup', finish);
          document.removeEventListener('pointercancel', finish);
          clearBuilderTrackOverlayHighlight(trackOverlayEl);
          clearBuilderPalettePlaceholder(grid);
          try {
            itemEl.releasePointerCapture?.(ev.pointerId);
          } catch {
            /* ignore */
          }
          ghost?.remove();

          if (!dragging) {
            onDrop?.({ def, autoPosition: true });
            return;
          }

          if (!isPointInside(grid.el, ev.clientX, ev.clientY)) return;

          if (lastSlot) {
            onDrop?.({ def, x: lastSlot.x, y: lastSlot.y });
            return;
          }

          onDrop?.({ def, clientX: ev.clientX, clientY: ev.clientY });
        };

        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', finish);
        document.addEventListener('pointercancel', finish);
      };

      itemEl.addEventListener('pointerdown', onPointerDown);
      cleanups.push(() => itemEl.removeEventListener('pointerdown', onPointerDown));
    });
  };

  bind();

  return {
    remount: bind,
    teardown,
  };
}
